import React, { useId, useState } from "react";
import { Sparkles, Send, History } from "lucide-react";
import {
  AppButton, Callout, Checkbox, FormField, NativeSelect, PhoneInput, StatusBadge,
} from "../ui";

/**
 * What only the server-run automations have (vc_be
 * services/whatsappAutomationService): whether the event can send right now and
 * why not, its own options, the suggested template, a test send, and what it
 * sent lately.
 *
 * The status describes the SAVED settings — the server decides readiness from
 * what is stored — so while there are unsaved edits it asks for a save instead
 * of showing an answer about settings that are no longer on screen.
 */

const fmtWhen = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch {
    return "";
  }
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="tw-flex tw-flex-col tw-gap-2">
    <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-content">
      {Icon && <Icon size={14} aria-hidden="true" />} {title}
    </h4>
    {children}
  </section>
);

/** The template body with each {{n}} replaced by its sample, as a student would read it. */
const sampleText = (t) => String(t.body || "").replace(/\{\{(\d+)\}\}/g, (whole, n) => t.example?.[Number(n) - 1] ?? whole);

/**
 * One ready-made template: what it is for, how it reads, and where it stands on
 * the institute's own WhatsApp account. One that Meta rejected or disabled
 * cannot be submitted again under the same name, so it offers no button.
 */
const ReadyMadeTemplate = ({ template: t, metaStatus, inUse, busy, disabled, onUse }) => {
  const blocked = metaStatus === "REJECTED" || metaStatus === "DISABLED";
  return (
    <article className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3">
      <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
        <div className="tw-min-w-0">
          <h5 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">{t.label}</h5>
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">{t.description}</p>
        </div>
        {metaStatus && <StatusBadge status={metaStatus} domain="whatsappTemplate" />}
      </div>
      <div className="tw-whitespace-pre-wrap tw-rounded-sm tw-bg-surface-alt tw-p-2.5 tw-text-sm tw-text-content">
        {sampleText(t)}
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-text-xs tw-text-content-subtle">Template name: {t.name}</span>
        {inUse ? (
          <span className="tw-text-xs tw-font-medium tw-text-success">In use for this automation</span>
        ) : blocked ? (
          <span className="tw-text-xs tw-text-danger">
            {metaStatus === "REJECTED" ? "Rejected by Meta" : "Disabled by Meta"} — delete it in Templates to submit it again.
          </span>
        ) : onUse && (
          <AppButton size="sm" variant="outline" onClick={onUse} disabled={disabled}>
            {busy ? "Working…" : metaStatus ? "Use this template" : "Create and use"}
          </AppButton>
        )}
      </div>
    </article>
  );
};

export default function AutomationEventPanel({
  def, state, patch, dirty, status, catalog, offsetMenu, templateStatus,
  onUseSuggested, suggestedBusy, onTest, testBusy,
}) {
  const [testPhone, setTestPhone] = useState("");
  const scopeId = useId();
  const testId = useId();
  const readyMade = catalog?.templates || [];
  const offsets = Array.isArray(state.offsets) ? state.offsets : def.defaultOffsets || [];

  const toggleOffset = (minutes, on) => {
    const next = on ? [...new Set([...offsets, minutes])] : offsets.filter((m) => m !== minutes);
    patch({ offsets: next.sort((a, b) => b - a) });
  };

  return (
    <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-5">
      {/* ── Can it send? ── */}
      {dirty ? (
        <Callout tone="info">Save your changes to check whether this automation can send.</Callout>
      ) : status && (status.ready ? (
        <Callout tone="success">Ready — this sends from your WhatsApp number.</Callout>
      ) : (
        <Callout tone="warning">
          {status.reason}
          {def.key === "studentWelcome" && " Until this is ready, new students get no WhatsApp welcome — with your own number connected, nothing is sent from the platform's."}
        </Callout>
      ))}

      {/* ── Its own options ── */}
      {def.reminder && (
        <Section title="When to remind">
          <div className="tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-2">
            {(offsetMenu || []).map((o) => (
              <Checkbox
                key={o.minutes}
                label={o.label}
                checked={offsets.includes(o.minutes)}
                onChange={(e) => toggleOffset(o.minutes, e.target.checked)}
              />
            ))}
          </div>
          {offsets.length === 0 && (
            <p className="tw-m-0 tw-text-xs tw-text-warning">No reminder is chosen, so none will be sent.</p>
          )}
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">{def.reminderHint}</p>
        </Section>
      )}

      {def.scoped && (
        <FormField label="Which quizzes and exams" htmlFor={scopeId}>
          <NativeSelect id={scopeId} value={state.scope || "all"} onChange={(e) => patch({ scope: e.target.value })}>
            <option value="all">All quizzes and exams</option>
            <option value="exams">Exams only — leave out practice quizzes</option>
          </NativeSelect>
        </FormField>
      )}

      {def.key === "enquiryReceived" && (
        <Checkbox
          label="Only enquiries that come in online"
          description="Leave out enquiries your team adds by hand on the Enquiries page."
          checked={!!state.skipManual}
          onChange={(e) => patch({ skipManual: e.target.checked })}
        />
      )}

      {/* ── Templates that fit ── */}
      {readyMade.length > 0 && (
        <Section icon={Sparkles} title="Ready-made templates">
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">
            Pick one and we send it to Meta for approval on your WhatsApp account and fill in what each
            variable sends. Meta usually reviews it within minutes; it sends once approved and this automation is on.
          </p>
          <div className="tw-flex tw-flex-col tw-gap-3">
            {readyMade.map((t) => (
              <ReadyMadeTemplate
                key={t.id}
                template={t}
                metaStatus={templateStatus?.[t.name]}
                inUse={state.templateName === t.name}
                busy={suggestedBusy === t.id}
                disabled={!!suggestedBusy}
                onUse={onUseSuggested ? () => onUseSuggested(t.id) : undefined}
              />
            ))}
          </div>
        </Section>
      )}

      {/* ── Try it ── */}
      {onTest && (
      <Section icon={Send} title="Send a test">
        <FormField
          label="Mobile number"
          htmlFor={testId}
          hint={def.audience === "enquiry"
            ? "Sent now with the template and mapping above — saved or not — and your most recent enquiry's details."
            : "Sent now with the template and mapping above — saved or not — your most recently added student's details and sample assignment / quiz values."}
        >
          <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-2">
            <div className="tw-min-w-0 tw-flex-1">
              <PhoneInput id={testId} value={testPhone} onChange={setTestPhone} nativeValidation={false} />
            </div>
            <AppButton size="sm" onClick={() => onTest(testPhone)} disabled={testBusy || !testPhone}>
              {testBusy ? "Sending…" : "Send test"}
            </AppButton>
          </div>
        </FormField>
      </Section>
      )}

      {/* ── What it did ── */}
      {status && (
        <Section icon={History} title="Recent messages">
          <p className="tw-m-0 tw-text-xs tw-text-content-muted">
            Last 7 days: {(status.last7Days?.delivered || 0) + (status.last7Days?.read || 0)} delivered
            {" · "}{status.last7Days?.sent || 0} sent, not yet delivered · {status.last7Days?.failed || 0} failed
            {" · "}{status.last7Days?.skipped || 0} skipped (daily limit) · {status.last7Days?.queued || 0} waiting
          </p>
          {status.recent?.length ? (
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-divide-y tw-divide-line tw-p-0">
              {status.recent.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-py-2 tw-text-sm">
                  <span className="tw-font-medium tw-text-content">{r.name || "—"}</span>
                  <span className="tw-text-xs tw-text-content-subtle">{r.phone}</span>
                  <StatusBadge status={r.status} domain="automationMessage" />
                  <span className="tw-ml-auto tw-text-xs tw-text-content-subtle">{fmtWhen(r.at)}</span>
                  {r.error && <span className="tw-w-full tw-text-xs tw-text-danger">{r.error}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">Nothing sent yet.</p>
          )}
        </Section>
      )}
    </div>
  );
}
